import React, { useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/landing/Header";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import Footer from "@/components/landing/Footer";
import Breadcrumbs from "@/components/landing/Breadcrumbs";
import Seo from "@/components/Seo";
import { getConsent, setConsent } from "@/lib/consent";

const Section = ({ number, title, children }) => (
  <section className="border-b border-[hsl(var(--border))] py-10 last:border-none">
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
      <div className="lg:col-span-3">
        <p className="font-display text-3xl text-[hsl(var(--sand-dark))]">{number}</p>
        <h2 className="mt-1 font-display text-2xl text-[hsl(var(--ink))]">{title}</h2>
      </div>
      <div className="prose prose-neutral max-w-none text-[hsl(var(--ink-soft))] lg:col-span-9">
        {children}
      </div>
    </div>
  </section>
);

export const Cookies = () => {
  const [c, setC] = useState(getConsent());

  const update = (key) => {
    const next = setConsent({ [key]: !c[key] });
    if (next) setC(next);
  };

  return (
    <main data-testid="cookies-page" className="bg-background">
      <Seo
        title="Cookiebeleid | EquiSight"
        description="Welke cookies EquiSight gebruikt, hoe lang ze worden bewaard en hoe je jouw voorkeuren beheert."
        path="/cookies"
        breadcrumbs={[{ name: "Cookiebeleid", path: "/cookies" }]}
      />
      <AnnouncementBar />
      <Header />
      <Breadcrumbs items={[{ name: "Cookiebeleid", path: "/cookies" }]} />

      <section className="bg-background py-16 lg:py-24">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
            Juridisch
          </p>
          <h1 className="mt-4 font-display text-5xl font-semibold leading-[1.05] tracking-tight text-[hsl(var(--ink))] sm:text-6xl">
            Cookiebeleid
          </h1>
          <p className="mt-6 text-[hsl(var(--ink-soft))]">Laatst bijgewerkt: 11-05-2026</p>
          <p className="mt-4 max-w-3xl text-[hsl(var(--ink-soft))]">
            EquiSight gebruikt cookies en vergelijkbare technologieën om de
            website goed te laten functioneren, gebruik te meten en — wanneer je
            dat toestaat — relevante content te tonen.
          </p>
          <p className="mt-6">
            <Link to="/" className="text-sm text-[hsl(var(--sand-dark))] underline-offset-4 hover:underline">
              ← Terug naar home
            </Link>
          </p>
        </div>
      </section>

      <article className="bg-background pb-16">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <Section number="1" title="Wat zijn cookies?">
            <p>
              Cookies zijn kleine tekstbestanden die op je apparaat worden
              opgeslagen wanneer je een website bezoekt. EquiSight gebruikt
              daarnaast vergelijkbare technologieën zoals localStorage en
              sessionStorage om voorkeuren en sessie-informatie te bewaren.
            </p>
          </Section>

          <Section number="2" title="Welke categorieën gebruiken wij?">
            <p className="font-medium text-[hsl(var(--ink))]">Noodzakelijk</p>
            <p>
              Altijd actief. Zorgt dat de site werkt, formulieren ingestuurd
              kunnen worden en jouw cookie-keuze wordt onthouden. Hieronder
              vallen sessie-identificatie en de cookie-banner status.
            </p>
            <p className="mt-5 font-medium text-[hsl(var(--ink))]">Analytics</p>
            <p>
              Helpt ons begrijpen welke pagina's worden bezocht, welke
              campagnes werken en waar gebruikers afhaken. Wij verzamelen
              `page_view`, `cta_click` en `waitlist_signup` events met
              UTM-context.
            </p>
            <p className="mt-5 font-medium text-[hsl(var(--ink))]">Marketing</p>
            <p>
              Onthoudt jouw rol (bv. hoefsmid, eigenaar) en herkomst (UTM)
              tussen sessies, zodat we relevantere aanbiedingen kunnen tonen.
            </p>
          </Section>

          <Section number="3" title="Hoe lang worden ze bewaard?">
            <ul className="mt-3 list-disc space-y-1.5 pl-5 marker:text-[hsl(var(--sand-dark))]">
              <li>Sessie-cookies: verwijderd zodra je je browser sluit.</li>
              <li>Voorkeurscookies (zoals jouw cookie-keuze): 12 maanden.</li>
              <li>Analytics gegevens: maximaal 26 maanden, daarna geanonimiseerd.</li>
            </ul>
          </Section>

          <Section number="4" title="Derde partijen">
            <p>
              Op dit moment maakt EquiSight gebruik van eigen analytics
              (`/api/events`). Indien wij in de toekomst gebruik maken van
              externe diensten (zoals Google Analytics of Meta Pixel) zullen
              wij dit cookiebeleid bijwerken en opnieuw om jouw toestemming
              vragen.
            </p>
          </Section>

          <Section number="5" title="Jouw keuze nu">
            <p>Pas hieronder jouw voorkeuren aan. Wijzigingen zijn direct van kracht.</p>

            <div className="mt-6 space-y-3">
              <PrefRow title="Noodzakelijk" desc="Altijd actief." checked disabled />
              <PrefRow
                title="Analytics"
                desc="Help ons EquiSight verbeteren door gebruik te meten."
                checked={c.analytics}
                onChange={() => update("analytics")}
              />
              <PrefRow
                title="Marketing"
                desc="Onthoud rol en herkomst voor relevante aanbiedingen."
                checked={c.marketing}
                onChange={() => update("marketing")}
              />
            </div>

            {c.decided && c.timestamp && (
              <p className="mt-4 text-xs text-[hsl(var(--ink-soft))]">
                Laatst opgeslagen:{" "}
                {new Date(c.timestamp).toLocaleString("nl-NL")}
              </p>
            )}
          </Section>

          <Section number="6" title="Contact">
            <p>
              Vragen over dit cookiebeleid? Mail naar{" "}
              <a
                href="mailto:support@equisight.nl"
                className="text-[hsl(var(--forest))] underline-offset-4 hover:underline"
              >
                support@equisight.nl
              </a>
              .
            </p>
          </Section>
        </div>
      </article>

      <Footer />
    </main>
  );
};

const PrefRow = ({ title, desc, checked, onChange, disabled }) => (
  <label className="flex cursor-pointer items-start justify-between gap-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-secondary))] p-4">
    <span className="flex-1">
      <span className="font-medium text-[hsl(var(--ink))]">{title}</span>
      <span className="mt-1 block text-xs text-[hsl(var(--ink-soft))]">{desc}</span>
    </span>
    <span className="mt-1 inline-flex">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="peer sr-only"
      />
      <span
        className={`relative inline-block h-5 w-9 rounded-full transition-all ${
          checked ? "bg-[hsl(var(--forest))]" : "bg-[hsl(var(--ink-soft))]/30"
        } ${disabled ? "opacity-60" : ""}`}
      >
        <span
          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${
            checked ? "left-4" : "left-0.5"
          }`}
        />
      </span>
    </span>
  </label>
);

export default Cookies;
